"use client";

import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle, Edit3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CHAT_INBOX_HEADER_CHROME } from "@/components/chat/chat-sticky-header";
import { RealtimeChat } from "@/components/realtime-chat";
import { ChatInboxList } from "@/components/chat/ChatInboxList";
import { ChatComposeTab } from "@/components/chat/ChatComposeTab";
import { PhoneVerificationGate } from "./PhoneVerificationGate";
import { createClient } from "@/utils/supabase/client";
import { useOverlay } from "@/components/context/overlay-context";
import { Z } from "@/lib/overlay/z-scale";
import { useUnreadCount } from "@/hooks/use-unread-count";
import { ChatInboxSkeleton } from "./ChatInboxSkeleton";
import { ChatConversationSkeleton } from "./ChatConversationSkeleton";
import { cn } from "@/lib/utils";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { fetchChatOtherParticipantProfile } from "@/lib/chat/chat-participant-profile";

interface ChatTabSliderProps {
  open: boolean;
  onOpenChangeAction: (open: boolean) => void;
  currentUserId: string;
  mode?: "inbox" | "chat" | "compose";
  roomName?: string;
  username?: string;
  onChatSelect?: (roomName: string, username: string) => void;
  initialOtherProfile?: { username: string; avatar_url?: string | null } | null;
  initialDraft?: InitialDraft;
  forceDesktop?: boolean;
  counterpartyBanned?: boolean;
}

type InitialDraft = { text?: string; imageFile?: File | null; listing?: any; order?: any; sendImmediately?: boolean } | null;

export function ChatTabSlider({
  open,
  onOpenChangeAction,
  currentUserId,
  mode = "inbox",
  roomName,
  username,
  onChatSelect,
  initialOtherProfile,
  initialDraft: initialDraftProp,
  forceDesktop = false,
  counterpartyBanned = false,
}: ChatTabSliderProps) {
  const tChat = useTranslations("chat");
  const [mounted, setMounted] = useState(false);
  const [currentMode, setCurrentMode] = useState<"inbox" | "chat" | "compose">(mode);
  const [currentRoomName, setCurrentRoomName] = useState<string | undefined>(roomName);
  const [currentUsername, setCurrentUsername] = useState<string | undefined>(currentUserId);
  const [otherUserProfile, setOtherUserProfile] = useState<{ username: string; avatar_url?: string | null } | null>(null);
  const [initialDraft, setInitialDraft] = useState<InitialDraft>(null);
  const [isPhoneVerified, setIsPhoneVerified] = useState<boolean | null>(null);
  const [inboxHasConversations, setInboxHasConversations] = useState<boolean | null>(null);
  const { overlay, openPanel, closePanel } = useOverlay();
  const isChatPanelActive = overlay.panel.kind === "chat";
  const { markConversationRead } = useUnreadCount(currentUserId);
  const pathname = usePathname();
  const router = useRouter();
  const lastPathnameRef = useRef<string | null>(null);

  // Latest-refs (react-state-design §10 — avoid stale closures without
  // adding every read to the effect's dep array) so the claim/release effect
  // below only re-runs on `open` transitions —
  // NOT on every overlay change (that would re-dispatch `openPanel` whenever
  // ANY overlay state changed elsewhere, e.g. cart opening as a utility,
  // which would wrongly re-clear the utility slot every time — openPanel
  // always resets it, lib/overlay/overlay-state.ts).
  const overlayRef = useRef(overlay);
  overlayRef.current = overlay;
  const currentRoomNameRef = useRef(currentRoomName);
  currentRoomNameRef.current = currentRoomName;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) {
      lastPathnameRef.current = pathname;
      return;
    }
    if (lastPathnameRef.current && lastPathnameRef.current !== pathname) {
      onOpenChangeAction(false);
    }
    lastPathnameRef.current = pathname;
  }, [open, onOpenChangeAction, pathname]);

  useEffect(() => {
    if (!open) return;

    async function checkPhoneVerification() {
      const supabase = createClient();
      const { data: profile } = await supabase
        .from("profiles")
        .select("phone")
        .eq("id", currentUserId)
        .single();

      setIsPhoneVerified(Boolean(profile?.phone && profile.phone.trim() !== ""));
    }

    checkPhoneVerification();
  }, [open, currentUserId]);

  useEffect(() => {
    setCurrentMode(mode);
    setCurrentRoomName(roomName);
    setCurrentUsername(currentUserId);

    if (initialOtherProfile) {
      setOtherUserProfile(initialOtherProfile);
    }
    if (initialDraftProp) {
      setInitialDraft(initialDraftProp);
    }
    if (mode === "chat" && roomName && !initialOtherProfile) {
      const supabase = createClient();
      void fetchChatOtherParticipantProfile(supabase, currentUserId, roomName).then((profile) => {
        if (profile) setOtherUserProfile(profile);
      });
    }
  }, [mode, roomName, currentUserId, initialOtherProfile, initialDraftProp]);

  // Claim the shared chat panel slot when this instance opens, release it
  // when this instance closes itself. Guarded on `overlayRef.current` (not
  // a plain dependency) so an external steal — e.g. profile opening, which
  // replaces the panel slot per §C.1 policy — doesn't get clobbered back by
  // this effect running with a stale "I still own it" assumption; see the
  // render gate below (`open && isChatPanelActive`) for how that steal is
  // reflected visually without this effect needing to react to it.
  useEffect(() => {
    if (open) {
      openPanel({ kind: "chat", roomId: currentRoomNameRef.current });
    } else if (overlayRef.current.panel.kind === "chat") {
      closePanel();
    }
  }, [open, openPanel, closePanel]);

  const handleOpenConversation = (
    roomName: string,
    draft?: InitialDraft,
    otherProfile?: { username: string; avatar_url?: string | null }
  ) => {
    if (otherProfile) {
      setOtherUserProfile(otherProfile);
    } else {
      const supabase = createClient();
      void fetchChatOtherParticipantProfile(supabase, currentUserId, roomName).then((profile) => {
        if (profile) setOtherUserProfile(profile);
      });
    }

    setCurrentMode("chat");
    setCurrentRoomName(roomName);
    setCurrentUsername(currentUserId);
    setInitialDraft(draft || null);

    if (onChatSelect) {
      const participants = roomName.split(":");
      const otherUserId = participants.find((id: string) => id !== currentUserId);
      onChatSelect(roomName, otherUserId || "");
    }
  };

  const handleStartCompose = () => {
    setCurrentMode("compose");
  };

  const handleBackToInbox = async () => {
    if (currentRoomName) {
      markConversationRead(currentRoomName);
    }

    if (currentRoomName) {
      try {
        await fetch("/api/chat/delete-if-empty", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ roomName: currentRoomName }),
        });
      } catch {}
    }

    setCurrentMode("inbox");
    setCurrentRoomName(undefined);
    setCurrentUsername(undefined);
    setOtherUserProfile(null);
    setInitialDraft(null);
  };

  const handleClose = () => {
    if (currentRoomName) {
      markConversationRead(currentRoomName);
    }

    onOpenChangeAction(false);
    setTimeout(() => {
      setCurrentMode("inbox");
      setCurrentRoomName(undefined);
      setCurrentUsername(undefined);
      setOtherUserProfile(null);
      setInitialDraft(null);
    }, 300);
  };

  const renderInboxHeader = (showClose: boolean) => (
    <div className={cn("shrink-0", CHAT_INBOX_HEADER_CHROME)}>
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full border border-border">
            <MessageCircle className="h-4 w-4 text-foreground" />
          </div>
          <h1 className="text-xl font-semibold text-foreground">{tChat("messages")}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full bg-secondary hover:bg-accent"
            onClick={handleStartCompose}
          >
            <Edit3 className="h-4 w-4" />
          </Button>
          <Badge variant="secondary" className="rounded-full px-3 py-1 text-xs font-medium text-foreground">
            {tChat("beta")}
          </Badge>
          {showClose ? (
            <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-full hover:bg-accent" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );

  const renderDesktopSplit = () => (
    <div className="flex min-h-0 flex-1 flex-col md:flex-row">
      <div className="relative hidden min-h-0 min-w-0 flex-1 flex-col border-r border-border md:flex">
        {isPhoneVerified === false ? null : currentMode === "chat" && currentRoomName && currentUsername ? (
          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
            <RealtimeChat
              key={currentRoomName}
              roomName={currentRoomName}
              username={currentUserId}
              hideHeader={false}
              initialOtherProfile={otherUserProfile}
              onBack={handleBackToInbox}
              isDialog={true}
              dockToPanel
              initialDraft={initialDraft}
              counterpartyBanned={counterpartyBanned}
              onNavigateAway={(href) => {
                handleClose();
                router.push(href);
              }}
            />
          </div>
        ) : currentMode === "compose" ? (
          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
            <ChatComposeTab
              currentUserId={currentUserId}
              dockToPanel
              onOpenConversationAction={handleOpenConversation}
              onCancelAction={() => setCurrentMode("inbox")}
            />
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center bg-background px-6 py-10 text-center">
            {inboxHasConversations ? (
              <>
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-border bg-background">
                  <MessageCircle className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground">{tChat("selectConversationTitle")}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{tChat("selectConversationHint")}</p>
              </>
            ) : null}
          </div>
        )}
      </div>

      <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden md:h-full md:w-[min(420px,40vw)] md:max-w-[420px] md:shrink-0 md:flex-none">
        {renderInboxHeader(false)}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <ChatInboxList
            layout="split"
            activeRoomName={currentMode === "chat" ? currentRoomName ?? null : null}
            currentUserId={currentUserId}
            onOpenConversationAction={handleOpenConversation}
            onCloseAction={handleClose}
            onComposeAction={handleStartCompose}
            onHasConversationsChange={setInboxHasConversations}
          />
        </div>
      </div>
    </div>
  );

  // §C.1 mutual exclusion: if another panel (e.g. profile) has taken the
  // shared slot since this instance last claimed it, `isChatPanelActive`
  // goes false and this instance hides — even though its own `open` prop
  // (owned by the caller) is still stale-true. This is the "opening profile
  // while chat is open replaces it" behavior (spec §E step 1.2).
  const isVisible = open && isChatPanelActive;

  const content = (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "tween", duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          className={cn(
            "fixed top-0 right-0 flex h-dvh w-full flex-col bg-background",
            forceDesktop
              ? "md:left-0 md:right-20 md:h-dvh md:w-auto md:max-w-none md:shadow-xl"
              : "md:hidden"
          )}
          style={{ zIndex: Z.overlayPanel }}
        >
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:min-h-0">
            {isPhoneVerified === null ? (
              currentMode === "chat" ? (
                <ChatConversationSkeleton />
              ) : (
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  {renderInboxHeader(!forceDesktop)}
                  <div className="min-h-0 flex-1 overflow-hidden">
                    <ChatInboxSkeleton layout="split" onComposeAction={handleStartCompose} onCloseAction={handleClose} />
                  </div>
                </div>
              )
            ) : forceDesktop ? (
              renderDesktopSplit()
            ) : (
              <AnimatePresence mode="wait" initial={false}>
                {currentMode === "inbox" ? (
                  <motion.div
                    key="inbox"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                    className="flex h-full min-h-0 flex-col overflow-hidden"
                  >
                    {renderInboxHeader(true)}
                    <div className="min-h-0 flex-1 overflow-hidden">
                      <ChatInboxList
                        layout="split"
                        activeRoomName={currentRoomName ?? null}
                        currentUserId={currentUserId}
                        onOpenConversationAction={handleOpenConversation}
                        onCloseAction={handleClose}
                        onComposeAction={handleStartCompose}
                      />
                    </div>
                  </motion.div>
                ) : currentMode === "chat" ? (
                  <motion.div
                    key="chat"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                    className="h-full"
                  >
                    {currentRoomName && currentUsername && (
                      <RealtimeChat
                        key={currentRoomName}
                        roomName={currentRoomName}
                        username={currentUserId}
                        hideHeader={false}
                        initialOtherProfile={otherUserProfile}
                        onBack={handleBackToInbox}
                        isDialog={true}
                        initialDraft={initialDraft}
                        counterpartyBanned={counterpartyBanned}
                        onNavigateAway={(href) => {
                          handleClose();
                          router.push(href);
                        }}
                      />
                    )}
                  </motion.div>
                ) : (
                  <motion.div
                    key="compose"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                    className="h-full"
                  >
                    <ChatComposeTab
                      currentUserId={currentUserId}
                      onOpenConversationAction={handleOpenConversation}
                      onCancelAction={() => setCurrentMode("inbox")}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </div>

          {isPhoneVerified === false && (
            <PhoneVerificationGate
              onVerifiedAction={() => {
                setIsPhoneVerified(true);
              }}
              onCloseAction={handleClose}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (!mounted) return null;
  return createPortal(content, document.body);
}
